import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X } from "react-feather";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import styles from "../vehiclerequests.module.css";
import { REQUEST_TYPE_LABELS, STATUS_LABELS, apiErrorMessage, formatDateTime } from "../vehicleRequestUtils";

const COMPARE_FIELDS = [
  { key: "license", label: "License plate" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "color", label: "Color" },
];

// Guests pay only after approval: nothing to show before, then unpaid / paid with validity
const guestPassText = (details) => {
  const pass = details?.pass;
  if (!pass) return details?.status === "PENDING" ? "Guest pays a Day or Monthly pass after approval" : "-";
  if (pass.is_paid) return `${pass.label} · $${Number(pass.amount || 0).toFixed(2)} paid${pass.expires_at ? ` · valid until ${formatDateTime(pass.expires_at)}` : ""}`;
  if (details.display_status === "PAYMENT_EXPIRED") return "Pay link expired without payment";
  return `Pay link sent, not paid yet${pass.pay_link_expires_at ? ` (link expires ${formatDateTime(pass.pay_link_expires_at)})` : ""}`;
};

const ReviewRequestDialog = ({ requestId, onClose, onReviewed }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");

  const loadDetails = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + requestId, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetails(response?.data?.data || null);
    } catch (error) {
      console.error("[VehicleRequests] Failed to load request details", error);
      setLoadError(apiErrorMessage(error, "Unable to load this request. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, requestId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const submitDecision = async (action) => {
    if (action === "reject" && reason.trim() === "") {
      setActionError("Please enter a reason for rejecting this request.");
      return;
    }

    setSubmitting(true);
    setActionError("");
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.post(
        process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + requestId + "/" + action,
        action === "reject" ? { reason: reason.trim() } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onReviewed(response?.data?.message || (action === "approve" ? "Request approved." : "Request rejected."));
    } catch (error) {
      console.error(`[VehicleRequests] Failed to ${action} request`, error);
      setActionError(apiErrorMessage(error, `Unable to ${action} the request. Please try again.`));
    } finally {
      setSubmitting(false);
    }
  };

  const isPending = details?.status === "PENDING";
  // Guest invites and guest updates; updates (tenant or guest) compare against the current vehicle
  const isGuest = details?.type === "GUEST" || details?.type === "GUEST_UPDATE";
  const isUpdate = details?.type === "TENANT_UPDATE" || details?.type === "GUEST_UPDATE";
  const current = details?.current_vehicle;
  const tenantName = [details?.tenant?.first_name, details?.tenant?.last_name].filter(Boolean).join(" ");

  return (<Dialog open onClose={submitting ? undefined : onClose} fullWidth maxWidth="md" className="NewPopUp MobileForm">
    <DialogTitle>
      {details ? REQUEST_TYPE_LABELS[details.type] || "Vehicle request" : "Vehicle request"}
      <button onClick={onClose} disabled={submitting}><X /></button>
    </DialogTitle>
    <DialogContent className={styles.dialogBody}>
      {loading && (<div className={styles.centerBox}><CircularProgress size={28} /></div>)}

      {!loading && loadError !== "" && (<div className={styles.stateBox}>
        <p>{loadError}</p>
        <Button onClick={loadDetails}>Try again</Button>
      </div>)}

      {!loading && details && (<>
        <div className={styles.detailGrid}>
          <div><span className={styles.detailLabel}>Status</span><span className={`${styles.statusBadge} ${styles["status" + details.status] || ""}`}>{STATUS_LABELS[details.status] || details.status}</span></div>
          <div><span className={styles.detailLabel}>Submitted</span>{formatDateTime(details.submitted_at)}</div>
          <div><span className={styles.detailLabel}>Property / Unit</span>{details.property_name || "-"}{details.apt_no ? ` · ${details.apt_no}` : ""}</div>
          <div><span className={styles.detailLabel}>{isGuest ? "Invited by" : "Tenant"}</span>{tenantName || "-"}</div>
          {!isGuest && (<div><span className={styles.detailLabel}>Tenant contact</span>{[details?.tenant?.phone, details?.tenant?.email].filter(Boolean).join(" · ") || "-"}</div>)}
          {isGuest && (<div><span className={styles.detailLabel}>Guest</span>{[details.guest_fname, details.guest_lname].filter(Boolean).join(" ")} · {details.guest_phone}</div>)}
          {details.type === "GUEST" && (<div><span className={styles.detailLabel}>Parking fee</span>
            {guestPassText(details)}
          </div>)}
          {details.reviewed_at && (<div><span className={styles.detailLabel}>Reviewed</span>{formatDateTime(details.reviewed_at)}{details.reviewed_by_name ? ` by ${details.reviewed_by_name}` : ""}</div>)}
          {details.review_note && (<div className={styles.detailFull}><span className={styles.detailLabel}>Reason</span>{details.review_note}</div>)}
        </div>

        <p className={styles.sectionTitle}>Vehicle details</p>
        <table className={styles.compareTable}>
          <thead>
            <tr>
              <th></th>
              {isUpdate && <th>Current</th>}
              <th>{isUpdate ? "Requested" : "Submitted"}</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_FIELDS.map((field) => {
              const changed = isUpdate && (current?.[field.key] || "") !== (details[field.key] || "");
              return (<tr key={field.key}>
                <td className={styles.compareLabel}>{field.label}</td>
                {isUpdate && <td>{current?.[field.key] || "-"}</td>}
                <td className={changed ? styles.changedValue : ""}>{details[field.key] || "-"}</td>
              </tr>);
            })}
          </tbody>
        </table>
        {isUpdate && !current && (<Alert severity="warning" className={styles.inlineAlert}>The vehicle being updated no longer exists.</Alert>)}

        <p className={styles.sectionTitle}>Photos</p>
        <div className={styles.photoGrid}>
          {isUpdate && current?.image_path && (<a href={current.image_path} target="_blank" rel="noopener noreferrer" className={styles.photoItem}>
            <img src={current.image_path} alt="Current vehicle" />
            <span>Current</span>
          </a>)}
          {(details.photos || []).map((url, index) => (<a key={url} href={url} target="_blank" rel="noopener noreferrer" className={styles.photoItem}>
            <img src={url} alt={`Submitted vehicle ${index + 1}`} />
            <span>{index === 0 ? "New · main photo" : "New"}</span>
          </a>))}
          {(details.photos || []).length === 0 && !(isUpdate && current?.image_path) && (<p className={styles.mutedText}>No photos were uploaded.</p>)}
        </div>

        {isPending && rejectMode && (<div className={styles.rejectBox}>
          <TextField
            label="Reason for rejecting*"
            multiline
            minRows={3}
            fullWidth
            value={reason}
            inputProps={{ maxLength: 500 }}
            onChange={(e) => setReason(e.target.value)}
            helperText="The requester will be notified by SMS." 
          />
        </div>)}

        {actionError !== "" && (<Alert severity="error" className={styles.inlineAlert}>{actionError}</Alert>)}
      </>)}
    </DialogContent>

    {!loading && details && isPending && (<DialogActions>
      {!rejectMode && (<>
        <Button className="CancelPopupBU" disabled={submitting} onClick={() => { setRejectMode(true); setActionError(""); }}>Reject</Button>
        <Button className="SubmitPopupBU" disabled={submitting} onClick={() => submitDecision("approve")}>{submitting ? "Approving..." : "Approve"}</Button>
      </>)}
      {rejectMode && (<>
        <Button className="SubmitPopupBU" disabled={submitting} onClick={() => { setRejectMode(false); setActionError(""); }}>Back</Button>
        <Button className="CancelPopupBU" disabled={submitting} onClick={() => submitDecision("reject")}>{submitting ? "Rejecting..." : "Confirm reject"}</Button>
      </>)}
    </DialogActions>)}
  </Dialog>);
};

export default ReviewRequestDialog;
